import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useFetchCapsules, useFetchCapsule } from 'new/stores/capsules/capsules.api';
import { selectCapsulesSlice } from 'new/stores/capsules/capsules.selector';
import type { AllowedClustersParams, CapsuleDto } from 'new/stores/capsules/capsules.endpoints';
import type { CapsuleEntity, CapsuleState } from 'new/stores/capsules/capsule.slice';

interface CapsulesRepositoryHookReturn {
  capsules: CapsuleState;
  getCapsuleById: (id: string) => CapsuleEntity | undefined;
  fetchCapsules: (params: AllowedClustersParams) => void;
  fetchCapsule: (id: string) => void;
  capsuleData: CapsuleDto | undefined;
  isCapsuleUninitialized: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
}

export default function useCapsulesRepository(): CapsulesRepositoryHookReturn {
  const capsules = useSelector(selectCapsulesSlice);
  const { fetchCapsules, isLoading, isFetching, isError, error } = useFetchCapsules();
  const {
    fetchCapsule,
    data: capsuleData,
    isLoading: isCapsuleLoading,
    isFetching: isCapsuleFetching,
    isUninitialized: isCapsuleUninitialized,
    isError: isCapsuleError,
    error: capsuleError,
  } = useFetchCapsule();
  const getCapsuleById = useCallback(
    (id: string): CapsuleEntity | undefined => capsules.entities[id],
    [capsules.entities],
  );

  return {
    capsules,
    getCapsuleById,
    fetchCapsules,
    fetchCapsule,
    capsuleData,
    isCapsuleUninitialized,
    isLoading: isLoading || isCapsuleLoading,
    isFetching: isFetching || isCapsuleFetching,
    isError: isError || isCapsuleError,
    error: error || capsuleError,
  };
}

export { useCapsulesRepository };
